import type Phaser from "phaser";
import type { TabViewContract, TabContentRect } from "@yjsgames/phaser-ui/tab-registry";
import type { Rect } from "@yjsgames/phaser-ui/panel-content";
import { SCROLLABLE_LIST_EVENT, ScrollableList } from "@yjsgames/phaser-ui";
import { FONT_FAMILY, setButtonEnabled, TYPO } from "./uiTokens.js";
import { t, getTextDirection } from "../i18n/i18n.js";
import type { TextDirection } from "@yjsgames/i18n";
import { formatBig, formatClock } from "./formatNumber.js";
import { computeShopLayout } from "./shopLayout.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { TipCarouselView } from "./TipCarouselView.js";
import { DIAMOND_BOX_STONE_DROPS } from "../data/balance.js";
import {
  ScrollInlineBannerSlot,
  shouldMountCrazyGamesInlineBannerAtRuntime,
} from "./inlineBannerPlaceholder.js";
import {
  shouldShowCrazyGamesBannerPlaceholder,
  shouldReserveCrazyGamesBannerSlot,
  subscribeCrazyGamesBannerReadinessChange,
} from "../ads/CrazyGamesBannerReadiness.js";
import {
  createShopCard,
  resolveShopDiscountBadgePlacement,
  resolveShopHelpPosition,
  setShopCardDimmed,
  type CardObjects,
} from "./shopCardObjects.js";
import type { ShopData, ShopAdRowData, ShopTabViewOptions } from "./ShopTabTypes.js";

const STONE_MIN = Math.min(...DIAMOND_BOX_STONE_DROPS.map((d) => d.amount));
const STONE_MAX = Math.max(...DIAMOND_BOX_STONE_DROPS.map((d) => d.amount));
const diamondBoxDescText = (): string =>
  t("shop.diamondBoxDesc", { min: STONE_MIN, max: STONE_MAX });
const STORE_PRODUCT_PENDING_LABEL = "준비중..";
const GOLD_BOX_10_DISCOUNT_PERCENT = 10;
const goldBox10DiscountLabel = (): string =>
  t("dailyShop.discountBadge", { percent: GOLD_BOX_10_DISCOUNT_PERCENT });

const SHOP_ROW_ICON_KEY = {
  gold: "icon_tab_codex",
  diamond: "icon_tab_stat",
  rebirthReset: "icon_rebirth_point",
  removeAds: "icon_no_ads",
} as const;

export class ShopTabView implements TabViewContract<ShopData> {
  private readonly tipView: TipCarouselView;
  private readonly scrollList: ScrollableList;
  private readonly cards: CardObjects[];
  private readonly resultText: Phaser.GameObjects.Text;
  private readonly showRemoveAds: boolean;
  private readonly adProducts: ShopTabViewOptions["adProducts"];
  private readonly bannerSlot: ScrollInlineBannerSlot;
  private readonly inlineBannerSupported: boolean;
  private inlineBannerEnabled: boolean;
  private visible = false;
  private rebirthInitialRequiredChapter = 0;
  private readonly rect: { x: number; y: number; width: number };
  private readonly handleBannerReadinessChange = (): void => this.applyBannerReadiness();

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    private readonly panelBottom: number,
    opts: ShopTabViewOptions,
  ) {
    this.rect = { x, y, width };
    this.showRemoveAds = opts.removeAdsEnabled ?? true;
    this.adProducts = opts.adProducts;
    const dir = getTextDirection();
    this.inlineBannerSupported = shouldMountCrazyGamesInlineBannerAtRuntime();
    this.inlineBannerEnabled = this.inlineBannerSupported && shouldReserveCrazyGamesBannerSlot();
    const L = computeShopLayout(
      { x, y, width, height: 0 },
      dir,
      this.cardCount(),
      this.panelBottom,
      { inlineBannerEnabled: this.inlineBannerEnabled },
    );

    this.tipView = new TipCarouselView(scene, L.tip.x, L.tip.y, L.tip.width, L.tip.height, "shop");
    this.tipView.setDepth(2);
    scene.add.existing(this.tipView);

    this.scrollList = new ScrollableList(scene, L.scroll.x, L.scroll.y, {
      width: L.scroll.width,
      height: L.scroll.height,
      barWidth: 5,
      barColor: PALETTE_NUM.scrollBar,
      trackColor: PALETTE_NUM.scrollTrack,
      trackAlpha: 0.65,
      barAlpha: 0.95,
      scrollSpeed: 36,
    });
    this.scrollList.setDepth(2);
    scene.add.existing(this.scrollList);

    this.bannerSlot = new ScrollInlineBannerSlot(
      "shop",
      scene,
      this.scrollList,
      L.bannerSlot,
      this.inlineBannerSupported,
    );
    this.bannerSlot.setPlaceholderVisible(shouldShowCrazyGamesBannerPlaceholder());
    this.scrollList.on(SCROLLABLE_LIST_EVENT.Scroll, () =>
      this.bannerSlot.sync(this.visible && this.inlineBannerEnabled),
    );
    const unsubscribeReadiness = subscribeCrazyGamesBannerReadinessChange(this.handleBannerReadinessChange);
    scene.events?.once?.("shutdown", unsubscribeReadiness);

    const cards = [
      createShopCard(
        scene,
        this.scrollList,
        L.cards[0]!,
        SHOP_ROW_ICON_KEY.gold,
        t("shop.goldBoxDesc"),
        "icon_gold",
        () => opts.onBuyGoldBox(),
        () => opts.onShowGoldBoxOdds(),
      ),
      createShopCard(
        scene,
        this.scrollList,
        L.cards[1]!,
        SHOP_ROW_ICON_KEY.gold,
        t("shop.goldBox10Desc"),
        "icon_gold",
        () => opts.onBuyGoldBox10(),
        () => opts.onShowGoldBoxOdds(),
        { discountLabel: goldBox10DiscountLabel() },
      ),
      createShopCard(
        scene,
        this.scrollList,
        L.cards[2]!,
        SHOP_ROW_ICON_KEY.diamond,
        diamondBoxDescText(),
        "icon_diamond",
        () => opts.onBuyDiamondBox(),
      ),
      createShopCard(
        scene,
        this.scrollList,
        L.cards[3]!,
        SHOP_ROW_ICON_KEY.rebirthReset,
        t("shop.rebirthConditionResetDesc", { chapter: 0 }),
        "icon_diamond",
        () => opts.onBuyRebirthConditionReset(),
      ),
    ];
    if (this.showRemoveAds) {
      cards.push(
        createShopCard(
          scene,
          this.scrollList,
          L.cards[4]!,
          SHOP_ROW_ICON_KEY.removeAds,
          t("iap.removeAds.desc"),
          "icon_diamond",
          () => opts.onBuyRemoveAds(),
        ),
      );
      cards.push(
        createShopCard(
          scene,
          this.scrollList,
          L.cards[5]!,
          SHOP_ROW_ICON_KEY.removeAds,
          t("iap.removeAdsPackage.desc"),
          "icon_diamond",
          () => opts.onBuyRemoveAdsPackage(),
        ),
      );
    }
    const adBase = cards.length;
    this.adProducts.forEach((p, i) => {
      cards.push(
        createShopCard(
          scene,
          this.scrollList,
          L.cards[adBase + i]!,
          p.iconKey,
          t(p.descKey, { amount: p.rewardAmount }),
          p.buttonIconKey,
          () => opts.onBuyAdDiamond(p.id),
        ),
      );
    });
    this.cards = cards;
    this.scrollList.setContentHeight(L.contentHeight);

    this.resultText = scene.add
      .text(L.result.x, L.result.y, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.body,
        color: PALETTE.positive,
        align: dir === "rtl" ? "right" : "left",
        wordWrap: { width },
      })
      .setOrigin(L.textOriginX, 0)
      .setDepth(2);
  }

  private baseCardCount(): number {
    return this.showRemoveAds ? 6 : 4;
  }

  private cardCount(): number {
    return this.baseCardCount() + this.adProducts.length;
  }

  private applyBannerReadiness(): void {
    this.bannerSlot.setPlaceholderVisible(shouldShowCrazyGamesBannerPlaceholder());
    const nextEnabled = this.inlineBannerSupported && shouldReserveCrazyGamesBannerSlot();
    if (nextEnabled === this.inlineBannerEnabled) {
      this.bannerSlot.sync(this.visible && this.inlineBannerEnabled);
      return;
    }
    this.inlineBannerEnabled = nextEnabled;
    this.relayout(getTextDirection());
  }

  /** 가이드퀘스트 하이라이트용 — 골드 상자 구매 버튼의 world rect. */
  goldBoxButtonRect(): Rect | null {
    if (!this.visible) return null;
    const bounds = this.goldCard.button.getBounds();
    return { x: bounds.x, y: bounds.y, width: bounds.width, height: bounds.height };
  }

  private get goldCard(): CardObjects {
    return this.cards[0]!;
  }
  private get gold10Card(): CardObjects {
    return this.cards[1]!;
  }
  private get diamondCard(): CardObjects {
    return this.cards[2]!;
  }
  private get removeAdsCard(): CardObjects {
    return this.cards[4]!;
  }
  private get removeAdsPackageCard(): CardObjects { return this.cards[5]!; }
  private get rebirthConditionResetCard(): CardObjects {
    return this.cards[3]!;
  }
  private adCardAt(i: number): CardObjects {
    return this.cards[this.baseCardCount() + i]!;
  }

  update(d: ShopData): void {
    this.goldCard.button.setLabel(formatBig(d.goldBoxCost));
    this.gold10Card.button.setLabel(formatBig(d.goldBox10Cost));
    this.diamondCard.button.setLabel(formatBig(d.diamondBoxCost));
    this.rebirthInitialRequiredChapter = d.rebirthInitialRequiredChapter;
    this.rebirthConditionResetCard.desc.setText(
      t("shop.rebirthConditionResetDesc", { chapter: d.rebirthInitialRequiredChapter }),
    );
    this.rebirthConditionResetCard.button.setLabel(formatBig(d.rebirthConditionResetCost));
    this.resultText.setText(d.resultLabel);
    setButtonEnabled(this.goldCard.button, d.goldBoxAffordable);
    setButtonEnabled(this.gold10Card.button, d.goldBox10Affordable);
    setButtonEnabled(this.diamondCard.button, d.diamondBoxAffordable);
    setButtonEnabled(
      this.rebirthConditionResetCard.button,
      d.rebirthConditionResetAffordable && d.rebirthConditionResetAvailable,
    );
    if (this.showRemoveAds) {
      if (d.adFree) {
        this.renderIapCard(this.removeAdsCard, true, t("iap.removeAds.owned"), false);
        this.renderIapCard(this.removeAdsPackageCard, true, t("iap.removeAds.owned"), false);
      } else {
        this.renderIapCard(this.removeAdsCard, d.removeAdsAvailable, d.removeAdsPriceLabel, true);
        this.renderIapCard(
          this.removeAdsPackageCard,
          d.removeAdsPackageAvailable,
          d.removeAdsPackagePriceLabel,
          true,
        );
      }
    }
    d.adRows.forEach((row, i) => this.renderAdCard(this.adCardAt(i), row));
  }

  private renderIapCard(
    card: CardObjects,
    available: boolean,
    priceLabel: string | undefined,
    canBuy: boolean,
  ): void {
    const enabled = available && canBuy && !!priceLabel;
    setShopCardDimmed(card, !enabled && canBuy);
    card.button.setIconVisible(false);
    card.button.setLabel(enabled ? priceLabel : priceLabel || STORE_PRODUCT_PENDING_LABEL);
    setButtonEnabled(card.button, enabled);
    card.button.setBgAlpha(!enabled && canBuy ? 0 : 1);
  }

  private renderAdCard(view: CardObjects, row: ShopAdRowData): void {
    if (row.processing) {
      view.button.setIconVisible(false);
      view.button.setLabel(t("shop.adDiamond.processing"));
    } else if (!row.gated) {
      view.button.setIconVisible(true);
      view.button.setLabel(t("shop.adDiamond.watch"));
    } else {
      view.button.setIconVisible(false);
      view.button.setLabel(
        row.gateKind === "cooldown" ? formatClock(row.remainMs) : t("shop.adDiamond.tomorrow"),
      );
    }
    setButtonEnabled(view.button, !row.gated && !row.processing);
  }

  applyLanguage(): void {
    this.tipView.applyLanguage();
    this.goldCard.desc.setText(t("shop.goldBoxDesc"));
    this.gold10Card.desc.setText(t("shop.goldBox10Desc"));
    this.gold10Card.discountBadge?.setText(goldBox10DiscountLabel());
    this.diamondCard.desc.setText(diamondBoxDescText());
    this.rebirthConditionResetCard.desc.setText(
      t("shop.rebirthConditionResetDesc", { chapter: this.rebirthInitialRequiredChapter }),
    );
    if (this.showRemoveAds) {
      this.removeAdsCard.desc.setText(t("iap.removeAds.desc"));
      this.removeAdsPackageCard.desc.setText(t("iap.removeAdsPackage.desc"));
    }
    this.adProducts.forEach((p, i) =>
      this.adCardAt(i).desc.setText(t(p.descKey, { amount: p.rewardAmount })),
    );
  }

  relayout(dir: TextDirection): void {
    const L = computeShopLayout(
      { ...this.rect, height: 0 },
      dir,
      this.cardCount(),
      this.panelBottom,
      { inlineBannerEnabled: this.inlineBannerEnabled },
    );
    this.tipView.setPosition(L.tip.x, L.tip.y);
    this.tipView.relayout(dir);
    const positions: { obj: Phaser.GameObjects.GameObject; lx: number; ly: number }[] = [];
    this.cards.forEach((card, i) => {
      const c = L.cards[i]!;
      card.desc.setWordWrapWidth(c.desc.wrapWidth);
      positions.push(
        { obj: card.bg, lx: c.bg.x, ly: c.bg.y },
        { obj: card.icon, lx: c.icon.x, ly: c.icon.y },
        { obj: card.desc, lx: c.desc.x, ly: c.desc.y },
        { obj: card.button, lx: c.button.x, ly: c.button.y },
      );
      if (card.help) {
        const help = resolveShopHelpPosition(c, Boolean(card.discountBadge));
        positions.push({ obj: card.help, lx: help.x, ly: help.y });
      }
      if (card.discountBadge) {
        const badge = resolveShopDiscountBadgePlacement(c);
        card.discountBadge.setOrigin(badge.originX, 0);
        positions.push({
          obj: card.discountBadge,
          lx: badge.x,
          ly: badge.y,
        });
      }
    });
    this.bannerSlot.pushRelayout(positions, L.bannerSlot);
    this.scrollList.relayoutContent(positions);
    this.scrollList.setContentHeight(L.contentHeight);
    this.bannerSlot.sync(this.visible && this.inlineBannerEnabled);
    this.resultText
      .setPosition(L.result.x, L.result.y)
      .setOrigin(L.textOriginX, 0)
      .setAlign(dir === "rtl" ? "right" : "left");
  }

  resize(rect: TabContentRect): void {
    this.rect.x = rect.x;
    this.rect.y = rect.y;
    this.rect.width = rect.width;
    const dir = getTextDirection();
    const L = computeShopLayout(
      { ...this.rect, height: 0 },
      dir,
      this.cardCount(),
      this.panelBottom,
      { inlineBannerEnabled: this.inlineBannerEnabled },
    );
    this.scrollList.resize(L.scroll.x, L.scroll.y, L.scroll.width, L.scroll.height);
    this.relayout(dir);
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    this.tipView.setVisible(visible);
    this.scrollList.setVisible(visible);
    this.resultText.setVisible(visible);
    this.bannerSlot.sync(visible && this.inlineBannerEnabled);
  }
}
